import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import { showModal, hideModal } from '../../../actions/modal.actions';
import { getModalData, getAccountJournal, getAccountJournalSummary, getUserPermission } from "../../../selectors";
import { getAccountJournalAction, getAccountJournalSummaryAction } from '../../../actions/accountDetailsActions/accountJournal.actions';
import CamvioTable2, {
  CamvioTable2Context,
  LoadMore,
  DefaultDashboardSearch,
  defineActionButton
} from 'src/components/UI/CamvioTable2';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import { groupStyles } from 'src/utils/SelectStyles';

class AccountJournalModal extends Component {
  state = {
    alertMessage: '',
    isPlanLoading: true,
    sortField: 'eventTimestamp',
    sortDirection: 'desc',
    showSummary: false,
    searchQuery: '',
    dateFilter: null,
    loadingSearch: false
  };

  debounceHandleInputChange = debounce((event) => {
    this.setState({
      searchQuery: event.target.value,
    });
  }, 300);

  componentDidMount() {
    const { modalProps } = this.props;
    if (modalProps && modalProps.accountId) {
      this.props.getAccountJournalAction(modalProps.accountId);
    }
  }

  componentWillUnmount() {}

  formatTimestamp = (eventTimestamp) => {
    if (!eventTimestamp) return { date: 'N/A', time: '' };
    const date = new Date(eventTimestamp.epochSecond * 1000);
    return {
      date: moment(date).format('MM/DD/YYYY'),
      time: moment(date).format('hh:mm:ss A')
    };
  }

  handleSummarize = () => {
    const { modalProps } = this.props;
    if (modalProps && modalProps.accountId) {
      this.setState({ showSummary: true });
      this.props.getAccountJournalSummaryAction(modalProps.accountId);
    }
  }

  onDashboardAction = (action, payload) => {
    console.log('onDashboardAction', payload);
    switch (action) {
      case 'SEARCH':
        this.setState({ searchQuery: this.state.tempSearchQuery });
      default:
        break;
    }
  }

  safeLower = (value) => {
    return value == null ? '' : String(value).toLowerCase();
  };

  setDateFilter = (value) => {
    this.setState({ dateFilter: value });
  }

  formatSummaryText = (text) => {
    try {
      const parsed = JSON.parse(text);
      return this.renderGenericStructuredData(parsed);
    } catch (e) {
      return this.renderGenericTextContent(text);
    }
  }

  renderGenericStructuredData = (data) => {
    return (
      <div>
        {Object.entries(data).map(([key, value], index) => {
          if (['accountId', 'generatedAt', 'journalEntriesCount', 'status'].includes(key)) {
            return null;
          }

          return (
            <div key={index} className="mb-4">
              <h6 className="text-primary mb-2">
                {this.formatFieldName(key)}
              </h6>
              <div className="summary-field-content">
                {this.renderFieldValue(value)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderGenericTextContent = (text) => {
    const sections = this.parseGenericSections(text);

    return (
      <div>
        {sections.map((section, index) => (
          <div key={index} className="mb-4">
            {section.title && (
              <h6 className="text-primary mb-2">
                {section.title}
              </h6>
            )}
            <div className="summary-field-content">
              {this.renderFormattedText(section.content)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  parseGenericSections = (text) => {
    const lines = text.split('\n');
    const sections = [];
    let currentSection = { title: null, content: [] };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === '') continue;

      if (/^#{1,6}\s/.test(line)) {

        if (currentSection.content.length > 0) {
          sections.push({
            title: currentSection.title,
            content: currentSection.content.join('\n')
          });
        }

        let title = line.replace(/^#{1,6}\s*/, '');
        // if (title.endsWith(':')) {
        //   title = title.slice(0, -1);
        // }
        if (title.startsWith('###')) {
          title = title.replace(/^#+\s*/, '');
        }
        currentSection = { title: title, content: [] };
      } else {
        currentSection.content.push(line);
      }
    }

    if (currentSection.content.length > 0) {
      sections.push({
        title: currentSection.title,
        content: currentSection.content.join('\n')
      });
    }

    if (sections.length === 0) {
      sections.push({
        title: null,
        content: text
      });
    }

    return sections;
  }

  formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  renderFieldValue = (value) => {
    if (Array.isArray(value)) {
      return (
        <ul className="mb-0">
          {value.map((item, index) => (
            <li key={index} className="mb-1">{item}</li>
          ))}
        </ul>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div>
          {Object.entries(value).map(([key, val], index) => (
            <div key={index} className="mb-2">
              <strong>{this.formatFieldName(key)}:</strong> {String(val)}
            </div>
          ))}
        </div>
      );
    } else {
      return this.renderFormattedText(String(value));
    }
  }

  onInputChange = (e) => {
    this.setState({ tempSearchQuery: e.target.value });
  };

  renderFormattedText = (text) => {
    if (!text) return null;

    return text.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      if (trimmedLine === '') return null;

      if (trimmedLine.startsWith('-')) {
        return (
          <div key={index} className="mb-1 ml-3">
            <span className="text-muted mr-2">•</span>
            {this.formatInlineMarkdown(trimmedLine.substring(1).trim())}
          </div>
        );
      }

      if (/^\d+\./.test(trimmedLine)) {
        return (
          <div key={index} className="mb-1 ml-3">
            {this.formatInlineMarkdown(trimmedLine)}
          </div>
        );
      }
      return (
        <p key={index} className="mb-2">
          {this.formatInlineMarkdown(trimmedLine)}
        </p>
      );
    }).filter(Boolean);
  }

  formatInlineMarkdown = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return <strong key={index}>{boldText}</strong>;
      }
      return part;
    });
  }


  sortList = (field, dir = null) => {
    if (field === null) dir = this.state.sortDirection === 'asc' ? 'desc' : 'asc';

    this.setState({
      sortField: field,
      sortDirection: dir,
    });
  };

  getTimezoneAbbrev = (date = new Date()) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const formatted = new Intl.DateTimeFormat([], {
      timeZoneName: "short"
    }).format(d);
    return formatted.split(" ").pop();
  };

  render() {
    const { alertMessage, sortField, sortDirection, showSummary, searchQuery, dateFilter, loadingSearch } = this.state;
    const { modalProps, accountJournal, accountJournalSummary, canSummarize } = this.props;
    const journalData = accountJournal.accountJournalInformation || [];


    let filteredJournalData = Array.isArray(journalData) ? journalData.slice() : [];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filteredJournalData = filteredJournalData.filter(entry => {
        return this.safeLower(entry.eventSummary).includes(q) ||
          this.safeLower(entry.createdBy).includes(q) ||
          this.safeLower(entry.eventCategory).includes(q);
      });
    }

    if (dateFilter) {
      const selectedDate = moment(dateFilter).format('YYYY-MM-DD');
      filteredJournalData = filteredJournalData.filter(entry => {
        if (!entry.eventTimestamp || !entry.eventTimestamp.epochSecond) return false;
        const entryDate = moment(entry.eventTimestamp.epochSecond * 1000).format('YYYY-MM-DD');
        return entryDate === selectedDate;
      });
    }

    if (sortField && sortDirection) {
      filteredJournalData.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (sortField === 'eventTimestamp') {
          aVal = aVal ? aVal.epochSecond : 0;
          bVal = bVal ? bVal.epochSecond : 0;
        } else {
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        }

        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }



    const isLoading = !!accountJournal.loading;
    const isDownloading = false;

    const timezoneAbbrev = new Intl.DateTimeFormat([], {
      timeZoneName: "short",
    })
      .format(new Date())
      .split(" ")
      .pop();



    return (
      <Fragment>
        <CamvioTable2Context.Provider value={this.state}>
          <div className="modal" style={{ display: 'block' }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-xl">
            <div className="modal-content" style={{ minHeight: '600px' }}>
              <div className="modal-header">
                <h5 className="modal-title">Account Journal</h5>
                <div className="d-flex align-items-center">
                  {canSummarize &&
                    <button
                      onClick={this.handleSummarize}
                      className="btn btn-sm mr-2"
                      disabled={accountJournal.summaryLoading}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '20px',
                        padding: '8px 16px',
                        fontWeight: '500',
                        boxShadow: '0 4px 15px 0 rgba(116, 75, 162, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!accountJournal.summaryLoading) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 20px 0 rgba(116, 75, 162, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px 0 rgba(116, 75, 162, 0.3)';
                      }}
                    >
                      <i className="fas fa-magic mr-1"></i>
                      {accountJournal.summaryLoading ? 'Summarizing...' : 'Summarize'}
                    </button>
                  }

                  <button onClick={this.props.hideModal} type="button" className="close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>

              <div className="modal-body">
                {showSummary && (
                  <div className="card mb-3">
                    <div className="card-header text-white" style={{ background: "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)"}}>
                      <h6 className="mb-0">
                        <i className="fas fa-magic mr-2"></i>
                        Journal Summary
                      </h6>
                    </div>
                    <div className="card-body">
                      {accountJournal.summaryLoading && (
                        <div className="text-center py-3">
                          <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Generating summary...</span>
                          </div>
                          <p className="mt-2 mb-0 text-muted">
                            <i className="fas fa-magic mr-1"></i>
                            AI is analyzing journal entries...
                          </p>
                        </div>
                      )}

                      {accountJournal.summaryError && (
                        <div className="alert alert-danger">
                          <i className="fas fa-exclamation-triangle mr-2"></i>
                          Error generating summary. Please try again.
                        </div>
                      )}

                      {!accountJournal.summaryLoading && accountJournalSummary && accountJournalSummary.summary && (
                        <div>
                          <div className="row mb-3">
                            <div className="col-md-4">
                              <small className="text-muted">Generated:</small>
                              <div>
                                {
                                  accountJournalSummary.generatedAt &&
                                  !isNaN(new Date(accountJournalSummary.generatedAt).getTime())
                                    ? new Intl.DateTimeFormat([], {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                      hour12: true,
                                      timeZoneName: "short"
                                    }).format(new Date(accountJournalSummary.generatedAt))
                                    : "N/A"
                                }
                              </div>

                              <small className="text-muted text-sm">
                                Duration: {accountJournalSummary.durationMillis
                                ? `${(accountJournalSummary.durationMillis / 1000).toFixed(2)}s`
                                : 'N/A'}
                              </small>
                            </div>
                            <div className="col-md-4">
                              <small className="text-muted">Entries Analyzed:</small>
                              <div>{accountJournalSummary.journalEntriesCount}</div>
                            </div>
                            <div className="col-md-4">
                              <small className="text-muted">Tokens Used:</small>
                              <div>{accountJournalSummary.usedInputTokens || 'N/A'}</div>
                            </div>
                          </div>
                          <hr />
                          <div className="summary-content">
                            {this.formatSummaryText(accountJournalSummary.summary)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {accountJournal.loading && (
                  <div className="text-center">
                    <i className="fas fa-spinner fa-spin"></i> Loading...
                  </div>
                )}

                {accountJournal.error && (
                  <div className="alert alert-danger">
                    Error loading journal data
                  </div>
                )}

                {!accountJournal.loading && showSummary && (
                  <div className="card mb-3">
                    <div className="card-header bg-info text-white">
                      <h6 className="mb-0">
                        <i className="fas fa-book mr-2"></i>
                        Journal Entries
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="tab-pane camvio-table-search" style={{ borderRadius: "0.5rem" }}>
                        <DefaultDashboardSearch
                          onAction={this.onDashboardAction}
                          onInputChange={this.onInputChange}
                          inputPlaceholder="Category, Summary or Created By..."
                          disabled={isLoading || loadingSearch || isDownloading}
                          hideSecondaryActionButtons={true}
                          showDownloadButton={false}
                        >
                          <div className="search">
                            <div className="form-row">
                              <div className="form-group col-md-6" style={{ maxWidth: 420 }}>
                                <label className="form-label text-muted mb-1">Journal Date</label>
                                <div
                                  className="input-group input-group-sm date"
                                  id="datetimepicker-journal-filter"
                                  data-target-input="nearest"
                                >
                                  <DatePicker
                                    className="form-control datetimepicker-input"
                                    selected={this.state.dateFilter}
                                    onChange={(value) => this.setDateFilter(value)}
                                    placeholderText="MM/DD/YYYY"
                                    name="dateFilter"
                                    isClearable={true}
                                    popperPlacement="bottom-start"
                                    style={{ width: '100%' }}
                                    maxDate={new Date()}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </DefaultDashboardSearch>
                      </div>

                      {Array.isArray(filteredJournalData) && filteredJournalData.length > 0 && (
                        <div className="mt-3">
                          <CamvioTable2
                            headerDefs={[
                              { name: 'Date/Time', field: 'eventTimestamp', sortable: true },
                              { name: 'Category', field: 'eventCategory', sortable: true },
                              { name: 'Summary', field: 'eventSummary', sortable: false }
                            ]}
                            sortFunction={this.sortList}
                            sortField={sortField}
                            sortDirection={sortDirection}
                          >
                            <tbody>
                            {filteredJournalData.map((entry, index) => {
                              const timestamp = this.formatTimestamp(entry.eventTimestamp);
                              return (
                                <tr key={index} className={index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                                  <td>
                                    <div>{timestamp.date}</div>
                                    <div>{timestamp.time} {timezoneAbbrev}</div>
                                  </td>
                                  <td>
                                    <div>
                                      <span className="badge badge-secondary">{entry.eventCategory}</span>
                                    </div>
                                    <div className="text-muted text-sm">{entry.createdBy || 'System'}</div>
                                  </td>
                                  <td>{entry.eventSummary}</td>
                                </tr>
                              );
                            })}
                            </tbody>
                          </CamvioTable2>
                        </div>
                      )}

                      {(!filteredJournalData || filteredJournalData.length === 0) && (
                        <div className="text-center text-muted mt-3">No journal entries found for this account.</div>
                      )}
                    </div>
                  </div>
                )}

                {!accountJournal.loading && !showSummary && (
                  <Fragment>
                    <div className="tab-pane camvio-table-search" style={{ borderRadius: "0.5rem" }}>
                      <DefaultDashboardSearch
                        onAction={this.onDashboardAction}
                        onInputChange={this.onInputChange}
                        inputPlaceholder="Category, Summary or Created By..."
                        disabled={isLoading || loadingSearch || isDownloading}
                        hideSecondaryActionButtons={true}
                        showDownloadButton={false}
                      >
                        <div className="search">
                          <div className="form-row">
                            <div className="form-group col-md-6" style={{ maxWidth: 420 }}>
                              <label className="form-label text-muted mb-1">Journal Date</label>
                              <div
                                className="input-group input-group-sm date"
                                id="datetimepicker-journal-filter"
                                data-target-input="nearest"
                              >
                                <DatePicker
                                  className="form-control datetimepicker-input"
                                  selected={this.state.dateFilter}
                                  onChange={(value) => this.setDateFilter(value)}
                                  placeholderText="MM/DD/YYYY"
                                  name="dateFilter"
                                  isClearable={true}
                                  popperPlacement="bottom-start"
                                  style={{ width: '100%' }}
                                  maxDate={new Date()}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </DefaultDashboardSearch>
                    </div>

                    {Array.isArray(filteredJournalData) && filteredJournalData.length > 0 && (
                      <CamvioTable2
                        headerDefs={[
                          { name: 'Date/Time', field: 'eventTimestamp', sortable: true },
                          { name: 'Category', field: 'eventCategory', sortable: true },
                          { name: 'Summary', field: 'eventSummary', sortable: false }
                        ]}
                        sortFunction={this.sortList}
                        sortField={sortField}
                        sortDirection={sortDirection}
                      >
                        <tbody>
                        {filteredJournalData.map((entry, index) => {
                          const timestamp = this.formatTimestamp(entry.eventTimestamp);
                          return (
                            <tr key={index} className={index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                              <td>
                                <div>{timestamp.date}</div>
                                <div>{timestamp.time} {timezoneAbbrev}</div>
                              </td>
                              <td>
                                <div>
                                  <span className="badge badge-secondary">{entry.eventCategory}</span>
                                </div>
                                <div className="text-muted text-sm">{entry.createdBy || 'System'}</div>
                              </td>
                              <td>{entry.eventSummary}</td>
                            </tr>
                          );
                        })}
                        </tbody>
                      </CamvioTable2>
                    )}

                    {(!filteredJournalData || filteredJournalData.length === 0) && (
                      <div className="text-center text-muted mt-3">No journal entries found for this account.</div>
                    )}
                  </Fragment>
                )}


              </div>
            </div>
          </div>
          </div>
        </CamvioTable2Context.Provider>
        <div className="modal-backdrop show" tabIndex="1" />

        <style jsx>{`
            .table-row-even {
                background-color: #f8f9fa;
            }
            .table-row-odd {
                background-color: #ffffff;
            }
            .table-row-even:hover,
            .table-row-odd:hover {
                background-color: #e9ecef;
            }
            .summary-content p {
                text-align: justify;
                line-height: 1.6;
            }
            .badge-lg {
                font-size: 0.875rem;
                padding: 0.5rem 0.75rem;
                font-weight: 500;
            }
        `}</style>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const modalProps = getModalData(state).modalProps;
  const accountJournal = getAccountJournal(state);
  const accountJournalSummary = getAccountJournalSummary(state);
  const canSummarize = getUserPermission(state, "CAM", "CAM_SUMMARIZE_ACC_JOURNAL");

  return {
    modalProps,
    accountJournal,
    accountJournalSummary,
    canSummarize
  };
};

const mapDispatchToProps = {
  hideModal,
  showModal,
  getAccountJournalAction,
  getAccountJournalSummaryAction
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountJournalModal);
